<script setup lang="ts">
import type { CreateForeignMachineryAccessory, MachineryAccessoryCategory } from '~/types'

const props = defineProps<{
  context: {
    value: CreateForeignMachineryAccessory[]
    node: { input: (v: CreateForeignMachineryAccessory[]) => unknown }
    options: MachineryAccessoryCategory[]
  } & Record<string, unknown>
}>()

function isCategorySelected(category: MachineryAccessoryCategory) {
  return props.context.value.some(item => item.category === category)
}

function toggleCategory(category: MachineryAccessoryCategory) {
  if (isCategorySelected(category)) {
    props.context.node.input(props.context.value.filter(item => item.category !== category))
    return
  }
  props.context.node.input([...props.context.value, { category }])
}

function updateValue(category: MachineryAccessoryCategory, key: string, value: number | null) {
  props.context.node.input(props.context.value.map((item) => {
    if (item.category === category) {
      return { ...item, [key]: value }
    }
    return item
  }))
}
</script>

<template>
  <div class="space-y-2">
    <div v-for="category in context.options" :key="category" class="border py-2 px-4 rounded">
      <div class="flex items-center justify-between gap-2 my-1.5">
        <div class="flex items-center gap-3">
          <Icon :name="useMachineryAccessoryIcon()[category]" class="size-[25px]" />
          <span class="text-lg">
            {{ $t(`machineryAccessory.category.${category}`) }}
          </span>
        </div>

        <div>
          <n-button
            type="primary"
            secondary
            class="size-10"
            @click="toggleCategory(category)"
          >
            <template #icon>
              <Icon
                :name="isCategorySelected(category) ? 'material-symbols:select-check-box-rounded' : 'material-symbols:check-box-outline-blank'"
                :size="30"
              />
            </template>
          </n-button>
        </div>
      </div>
      <div v-if="isCategorySelected(category)" class="grid grid-cols-2 gap-2">
        <template v-if="['forkAdjuster', 'sideShifter'].includes(category)">
          <label>
            <span>
              {{ $t('customerModule.machineryAccessory.distanceInnerEdgeToInnerEdge') }}
            </span>
            <n-input-number
              :disabled="!isCategorySelected(category)"
              @update:value="value => updateValue(category, 'distanceInnerEdgeToInnerEdge', value)"
            />
          </label>
          <label>
            <span>
              {{ $t('customerModule.machineryAccessory.distanceOuterEdgeToOuterEdge') }}
            </span>
            <n-input-number
              :disabled="!isCategorySelected(category)"
              @update:value="value => updateValue(category, 'distanceOuterEdgeToOuterEdge', value)"
            />
          </label>
        </template>
        <template v-if="['fork'].includes(category)">
          <label>
            <span>
              {{ $t('privateDetailsPage.machineryAccessory.creationInformationColumn.forkLength') }}
            </span>
            <n-input-number
              :disabled="!isCategorySelected(category)"
              @update:value="value => updateValue(category, 'lengthInMillimeters', value)"
            />
          </label>
          <label>
            <span>
              {{ $t('privateDetailsPage.machineryAccessory.creationInformationColumn.forkWidth') }}
            </span>
            <n-input-number
              :disabled="!isCategorySelected(category)"
              @update:value="value => updateValue(category, 'widthInMillimeters', value)"
            />
          </label>
          <label>
            <span>
              {{ $t('privateDetailsPage.machineryAccessory.creationInformationColumn.forkHeight') }}
            </span>
            <n-input-number
              :disabled="!isCategorySelected(category)"
              @update:value="value => updateValue(category, 'heightInMillimeters', value)"
            />
          </label>
        </template>
      </div>
    </div>
  </div>
</template>
